/** The two addresses that are the sign-in screen itself, never a place to return to. */
const SIGN_IN_PATHS = new Set(['/auth', '/auth-modal']);

/** An in-app path: starts with one slash, not two (`//host` is a protocol-relative URL). */
export const isInternalPath = (value: string): boolean => value.startsWith('/') && !value.startsWith('//');

/**
 * Where a successful sign-in returns to.
 *
 * An opener that pushes the sign-in screen says so in navigation state (`from`), and that
 * wins. A gated screen, though, renders the sign-in screen *in its own place* — someone who
 * opened `/rental/<id>` from an email with no session sees the sign-in form at that very
 * address, and there is no navigation state to read. The address bar already holds the
 * destination, so that is the return target: the deep link survives the sign-in
 * (ia-entry-points-kickoff.md §7, "an expired session does not lose its destination").
 *
 * Only in-app paths are ever returned: `from` is untrusted input at the edge, and an address
 * that is the sign-in screen itself would loop, so both fall back to home.
 */
export const resolveSignInReturnTo = (input: {
  from?: string;
  pathname: string;
  search?: string;
}): string | undefined => {
  const { from, pathname, search = '' } = input;

  if (from && isInternalPath(from)) {
    return from;
  }

  if (!SIGN_IN_PATHS.has(pathname) && isInternalPath(pathname)) {
    return `${pathname}${search}`;
  }

  return undefined;
};
